import { parseISO, isValid, format } from "date-fns";
import { DATE_FORMATS } from "@/utils/formatters/dateFormatter";
import { Reimbursement, StoplossClaim } from "@/types";

/**
 * Reporting-period scoping for the Client Claims Summary (#6823).
 *
 * The period binds to `stoploss_claims.created_datetime`, not `submitted_date`.
 * `submitted_date` is a carrier-submission date — it is stamped only by
 * MarkSubmittedToCarrier (`stoploss_claim_adapter.go`, `submitted_date = NOW()::DATE`),
 * so a claim whose activity landed in January but was filed with the carrier in
 * September reported in September, and prior-period client reports did not reproduce.
 * It is also NULL until a claim is submitted at all, which silently dropped every
 * unsubmitted claim from the Summary — the actual mechanism behind #5354, and the
 * cause of the Summary/Detail reconciliation gap in #6825.
 *
 * The subtlety: `created_datetime` is a TIMESTAMPTZ **instant**, while the range
 * inputs hold `yyyy-MM-dd`. `new Date("2026-01-01")` parses as UTC midnight, so
 * comparing the instant directly against a range bound puts the two sides of the
 * comparison in different frames and shifts the boundary by a day. Each instant is
 * therefore resolved to a calendar date first, and only then compared.
 *
 * Frame: the **reviewer's local calendar**, per the standard documented on
 * `todayLocalYMD` in `@/utils/formatters/dateFormatter` — `date-fns` `format`
 * resolves locally, which is what keeps both sides of the comparison in the same
 * frame as the `<input type="date">` bounds the user set.
 *
 * ## Accepted limitation: the frame is the reviewer's, not the report's
 *
 * A fixed reporting timezone was considered and rejected in favour of matching the
 * date inputs the user actually sees. The cost is explicit and accepted: because the
 * whole period filter runs in the browser, **two reviewers in different timezones can
 * export different claim sets for the same client period.** A claim created at
 * 2026-01-31T20:00-06:00 is 2026-02-01 in UTC, so it lands inside a January period for
 * a Central reviewer and outside it for one in UTC — and evening loads put that window
 * in routine use, not at an exotic edge.
 *
 * That is a *different* non-reproducibility from the one #6823 fixes (which was
 * temporal: the same reviewer got different answers as `submitted_date` moved). This
 * one is spatial and stable per reviewer. It is tolerable while the exhibit is produced
 * by a US-Central team; it stops being tolerable the moment the report is generated from
 * more than one timezone, or server-side. If that changes, the fix is to anchor the
 * comparison to a fixed policy/reporting timezone and stamp it in the sheet header so
 * the workbook says which calendar it used — not to revert to slicing the instant.
 */

/**
 * Resolves an ISO datetime to its `yyyy-MM-dd` calendar date in the reviewer's local
 * calendar. Returns null when the value is absent or unparseable, so callers decide
 * how to treat it rather than silently bucketing it into a period.
 *
 * Note this is deliberately NOT `toInputDate`, which slices the first 10 characters
 * off the string — that yields the **UTC** date for a timestamp and would reintroduce
 * the off-by-one this function exists to avoid. It is safe for the date-only columns
 * `toInputDate` is normally used on.
 */
export const toReportingDate = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const instant = parseISO(value);
  if (!isValid(instant)) return null;
  return format(instant, DATE_FORMATS.INPUT);
};

/**
 * True when a stop-loss claim's created date falls inside the selected reporting
 * period, inclusive of both ends.
 *
 * `startDate` / `endDate` are the `yyyy-MM-dd` values the date inputs hold, so this
 * is a lexicographic comparison on zero-padded calendar dates in the same frame.
 *
 * This is the **single** predicate governing claim inclusion for this report. The
 * Summary table, the Claims Detail table and the export's Claims Detail sheet all
 * route through it, so the three cannot drift apart the way the two hand-written
 * predicates did (#6825).
 */
export const isClaimInReportingPeriod = (
  claim: Pick<StoplossClaim, "created_datetime">,
  startDate: string,
  endDate: string
): boolean => {
  // An unset range means the period isn't resolved yet (no policy selected). Include
  // nothing rather than everything, matching the pre-#6823 behaviour.
  if (!startDate || !endDate) return false;

  const created = toReportingDate(claim.created_datetime);
  if (!created) return false;

  return created >= startDate && created <= endDate;
};

/**
 * The date a reimbursement reports under on this exhibit (#7560):
 * `payment_date ?? check_date ?? received_date`, or null when none is set.
 *
 * - `payment_date` is authoritative (#5310). It is the only date a document load
 *   writes: the reimbursement target schema has no `check_date` field, so "check
 *   date" columns map onto `payment_date`.
 * - `check_date` is the legacy field, still set by manual create/edit and mark-paid.
 * - `received_date` is the carrier's receipt date. It precedes payment, so a row
 *   dated by it can report in an earlier period than the one it was paid in. It is
 *   the last resort, ahead of dropping the row.
 *
 * The **single** precedence for this report. The Summary's dollar columns, the
 * Claims Detail expand row and both export sheets route through it, so they agree
 * on which date a reimbursement carries. The Summary scoped on `check_date` alone,
 * which zeroed every document-loaded reimbursement out of its claimant's row.
 */
export const reimbursementDate = (
  r: Pick<Reimbursement, "payment_date" | "check_date" | "received_date">
): string | null => r.payment_date ?? r.check_date ?? r.received_date ?? null;
